/** OneDrive 上のパス組み立て（仕様書 §4.2）。すべて App Folder からの相対パス。 */

import type { MonthKey } from '../domain/types';

export const SETTINGS_PATH = 'settings.json';
export const CATEGORIES_PATH = 'categories.json';
export const GOALS_PATH = 'goals.json';
export const TASKS_PATH = 'tasks.json';
export const PROJECTS_PATH = 'projects.json';

export function monthDir(month: MonthKey): string {
  return `${month.slice(0, 4)}/${month.slice(5, 7)}`;
}

export const indexPath = (month: MonthKey) => `${monthDir(month)}/index.json`;
export const entryPath = (month: MonthKey, id: string) => `${monthDir(month)}/entries/${id}.json`;
export const mediaPath = (month: MonthKey, file: string) => `${monthDir(month)}/media/${file}`;
export const strokesPath = (month: MonthKey, file: string) => `${monthDir(month)}/drawings/${file}`;
export const tombstonePath = (month: MonthKey) => `tombstones/${month}.json`;
export const dailyPath = (month: MonthKey, date: string) => `${monthDir(month)}/daily/${date}.json`;
export const reviewPath = (week: string) => `reviews/${week}.json`;
export const taskDailyPath = (month: MonthKey, date: string) => `${monthDir(month)}/tasks/${date}.json`;
/** 週は月をまたぐため、月フォルダの下には置かない。 */
export const taskWeeklyPath = (week: string) => `tasks/weekly/${week}.json`;

export function conflictPath(entryId: string, deviceId: string, iso: string): string {
  return `conflicts/${entryId}__${deviceId}__${iso.replace(/[:.]/g, '')}.json`;
}

/** 相対パスの種類。delta の結果を振り分けるのに使う。 */
export type PathKind =
  | { kind: 'entry'; month: MonthKey; id: string }
  | { kind: 'index'; month: MonthKey }
  | { kind: 'media'; month: MonthKey; file: string }
  | { kind: 'strokes'; month: MonthKey; file: string }
  | { kind: 'tombstone'; month: MonthKey }
  | { kind: 'daily'; month: MonthKey; date: string }
  | { kind: 'review'; week: string }
  | { kind: 'goals' }
  | { kind: 'tasks' }
  | { kind: 'projects' }
  | { kind: 'taskDaily'; month: MonthKey; date: string }
  | { kind: 'taskWeekly'; week: string }
  | { kind: 'categories' }
  | { kind: 'settings' }
  | { kind: 'conflict' }
  | { kind: 'other' };

const ENTRY_RE = /^(\d{4})\/(\d{2})\/entries\/([^/]+)\.json$/;
const INDEX_RE = /^(\d{4})\/(\d{2})\/index\.json$/;
const MEDIA_RE = /^(\d{4})\/(\d{2})\/media\/(.+)$/;
const STROKES_RE = /^(\d{4})\/(\d{2})\/drawings\/(.+)$/;
const TOMB_RE = /^tombstones\/(\d{4}-\d{2})\.json$/;
const DAILY_RE = /^(\d{4})\/(\d{2})\/daily\/(\d{4}-\d{2}-\d{2})\.json$/;
const REVIEW_RE = /^reviews\/(\d{4}-W\d{2})\.json$/;
const TASK_D_RE = /^(\d{4})\/(\d{2})\/tasks\/(\d{4}-\d{2}-\d{2})\.json$/;
const TASK_W_RE = /^tasks\/weekly\/(\d{4}-W\d{2})\.json$/;

export function classifyPath(rel: string): PathKind {
  let m: RegExpExecArray | null;
  if ((m = ENTRY_RE.exec(rel))) return { kind: 'entry', month: `${m[1]}-${m[2]}`, id: m[3] };
  if ((m = INDEX_RE.exec(rel))) return { kind: 'index', month: `${m[1]}-${m[2]}` };
  if ((m = MEDIA_RE.exec(rel))) return { kind: 'media', month: `${m[1]}-${m[2]}`, file: m[3] };
  if ((m = STROKES_RE.exec(rel))) return { kind: 'strokes', month: `${m[1]}-${m[2]}`, file: m[3] };
  if ((m = TOMB_RE.exec(rel))) return { kind: 'tombstone', month: m[1] };
  if ((m = DAILY_RE.exec(rel))) return { kind: 'daily', month: `${m[1]}-${m[2]}`, date: m[3] };
  if ((m = REVIEW_RE.exec(rel))) return { kind: 'review', week: m[1] };
  if ((m = TASK_D_RE.exec(rel))) return { kind: 'taskDaily', month: `${m[1]}-${m[2]}`, date: m[3] };
  if ((m = TASK_W_RE.exec(rel))) return { kind: 'taskWeekly', week: m[1] };
  if (rel === GOALS_PATH) return { kind: 'goals' };
  if (rel === TASKS_PATH) return { kind: 'tasks' };
  if (rel === PROJECTS_PATH) return { kind: 'projects' };
  if (rel === CATEGORIES_PATH) return { kind: 'categories' };
  if (rel === SETTINGS_PATH) return { kind: 'settings' };
  if (rel.startsWith('conflicts/')) return { kind: 'conflict' };
  return { kind: 'other' };
}
